import type { HardwareFieldError } from '~/utils/installation/Installation'
import { STORE_ORDER_SAVE_ERROR_CONFIG } from './storeOrderReason'

const SELECT_ERROR_KEYS: Record<string, string> = {
  STORE_ORDER_INVALID_FOR_COMBO_CONECTE: 'customer.equipment_step.store_order.errors.invalid_for_combo_conecte',
  STORE_ORDER_SIZE_INCOMPATIBLE_WITH_PROJECT: 'customer.equipment_step.store_order.errors.size_incompatible',
  STORE_ORDER_USED_ANOTHER: 'customer.equipment_step.store_order.errors.used_another',
}

const INVALID_FOR_PROJECT_TITLE_KEY = 'customer.equipment_step.store_order.errors.invalid_for_project_title'

// save_hardware errors where `detail` is a STRING (no reason_code), shaped like
// "[<order>][save_hardware] <English message>". The first [] is the order id (varies); the rest is fixed.
// We show the translated (i18n) text in the toaster and below the order field.
const SAVE_HARDWARE_MESSAGE_KEYS: Record<string, string> = {
  'Invalid power information for the hardware': 'customer.equipment_step.store_order.errors.invalid_power',
  'Invalid battery information for the hardware': 'customer.equipment_step.store_order.errors.invalid_battery',
}

// Granular hardware validation reason_codes (power/overload) on 422 from POST /hardware/save.
// Exactly one per response. Reuses manual kit i18n strings (`project_data.errors.*`) as single source of truth.
const SAVE_HARDWARE_REASON_CODE_KEYS: Record<string, string> = {
  HARDWARE_MODULES_SYSTEM_POWER_INVALID: 'project_data.errors.modules_system_power',
  HARDWARE_BATTERY_CAPACITY_INVALID: 'project_data.errors.battery_capacity',
  HARDWARE_MODULES_PRICE_PER_WATT_UNDER: 'project_data.errors.modules_price_per_watt_under',
  HARDWARE_MODULES_PRICE_PER_WATT_OVER: 'project_data.errors.modules_price_per_watt_over',
  HARDWARE_INVERTER_STRING_OVERLOAD_MIN: 'project_data.errors.string_overload_min',
  HARDWARE_INVERTER_STRING_OVERLOAD_MAX: 'project_data.errors.string_overload_max',
  HARDWARE_MICROINVERTER_OVERLOAD_MIN: 'project_data.errors.microinverter_overload_min',
  HARDWARE_MICROINVERTER_OVERLOAD_MAX: 'project_data.errors.microinverter_overload_max',
}

// Strip bracket prefixes: "[uuid][save_hardware] message" → "message".
function extractDetailMessage(detail: unknown): string {
  if (typeof detail !== 'string')
    return ''

  return detail.replace(/^\s*(?:\[[^\]]*\]\s*)+/, '').trim()
}

export type StoreOrderSaveError
  = | { kind: 'store-order-status', messageKey: string, markExpired?: boolean, showFieldError?: boolean }
    | { kind: 'select', messageKey: string }
    | { kind: 'alert', titleKey: string, message: string }
    | { kind: 'field-toast', messageKey: string }
    | { kind: 'unknown' }

export function parseSaveError(error: any): { reasonCode: string, message: string } {
  const data = error?.response?.data ?? error?.data
  const detail = data?.detail
  const reasonCode = detail?.reason_code ?? data?.reason_code ?? data?.code ?? ''
  const message = typeof detail === 'string' ? detail : (detail?.message ?? data?.message ?? '')

  return { reasonCode, message }
}

export function resolveStoreOrderSaveError(error: any): StoreOrderSaveError {
  const { reasonCode, message } = parseSaveError(error)

  const storeOrderStatusError = STORE_ORDER_SAVE_ERROR_CONFIG[reasonCode]
  if (storeOrderStatusError) {
    return {
      kind: 'store-order-status',
      messageKey: storeOrderStatusError.messageKey,
      markExpired: storeOrderStatusError.markExpired,
      showFieldError: storeOrderStatusError.showFieldError,
    }
  }

  if (SELECT_ERROR_KEYS[reasonCode])
    return { kind: 'select', messageKey: SELECT_ERROR_KEYS[reasonCode] }

  if (reasonCode === 'STORE_ORDER_INVALID_FOR_PROJECT')
    return { kind: 'alert', titleKey: INVALID_FOR_PROJECT_TITLE_KEY, message }

  if (SAVE_HARDWARE_REASON_CODE_KEYS[reasonCode])
    return { kind: 'field-toast', messageKey: SAVE_HARDWARE_REASON_CODE_KEYS[reasonCode] }

  const saveHardwareKey = SAVE_HARDWARE_MESSAGE_KEYS[extractDetailMessage(message)]
  if (saveHardwareKey)
    return { kind: 'field-toast', messageKey: saveHardwareKey }

  if (/not compatible with combo_connect/i.test(message))
    return { kind: 'select', messageKey: SELECT_ERROR_KEYS.STORE_ORDER_INVALID_FOR_COMBO_CONECTE }

  return { kind: 'unknown' }
}

export function getStoreOrderErrorMessageKey(reasonCode: string): string | undefined {
  return SELECT_ERROR_KEYS[reasonCode]
}

export function getOrderFieldError(errors?: HardwareFieldError[] | null): string | undefined {
  const orderFieldError = errors?.find(e => e.field === 'order_id')
  if (!orderFieldError)
    return undefined

  return orderFieldError.title || orderFieldError.description
}
